import { forwardRef, useMemo } from 'react'
import { EChart, type EChartHandle } from './EChart'
import { buildScatterOption, type ScatterInput, type ScatterPoint } from './scatterOption'
import { useChartTheme } from './useChartTheme'

export interface ScatterPlotProps extends ScatterInput {
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  /** Клик по точке — обычно панель детализации объекта. */
  onPointClick?: (point: ScatterPoint) => void
  /** Наведение на точку (`null` — ушли): подсветка строки таблицы. */
  onPointHover?: (key: string | null) => void
}

/**
 * Рассеяние «мера × мера» по объектам (расход × ДРР по кампаниям): не больше трёх цветов групп, порог — пунктиром,
 * размер точки — третья мера. Легенду групп показывайте рядом (`ChartLegend`), табличный вид — у `ChartCard`.
 */
export const ScatterPlot = forwardRef<EChartHandle, ScatterPlotProps>(function ScatterPlot(
  { height = 320, loading, ariaLabel, className, texture, onPointClick, onPointHover, ...input },
  ref,
) {
  const theme = useChartTheme()
  const option = useMemo(
    () => buildScatterOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      theme,
      input.points,
      input.groups,
      input.xLabel,
      input.yLabel,
      input.xFormat,
      input.yFormat,
      input.xAxisFormat,
      input.yAxisFormat,
      input.sizeLabel,
      input.sizeFormat,
      input.xLog,
      input.thresholdX,
      input.thresholdY,
    ],
  )
  const pointAt = (params: { data?: any } | null | undefined): ScatterPoint | undefined => {
    const index = params?.data?.pointIndex
    return typeof index === 'number' ? input.points[index] : undefined
  }

  return (
    <EChart
      ref={ref}
      option={option}
      height={height}
      loading={loading}
      ariaLabel={ariaLabel}
      className={className}
      texture={texture}
      onMarkClick={(params) => {
        if (params.componentType !== 'series') return
        const point = pointAt(params)
        if (point) onPointClick?.(point)
      }}
      onMarkHover={(params) => {
        if (!onPointHover) return
        onPointHover(pointAt(params)?.key ?? null)
      }}
    />
  )
})

export default ScatterPlot
